import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  NotificationItem,
  NotificationKind,
  NotificationsPage,
} from '@/redux/models';

export interface NotificationsSliceState {
  items: NotificationItem[];
  nextCursor: string | null;
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationsSliceState = {
  items: [],
  nextCursor: null,
  unreadCount: 0,
  loading: false,
  error: null,
};

// Personal and campaign ids come from different tables, so identity is the pair.
const sameItem = (
  a: { kind: NotificationKind; id: number },
  b: { kind: NotificationKind; id: number },
) => a.kind === b.kind && a.id === b.id;

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      loading: action.payload,
    }),
    setError: (state, action: PayloadAction<string | null>) => ({
      ...state,
      error: action.payload,
      loading: false,
    }),

    fetchNotifications: (
      state,
      action: PayloadAction<{ cursor?: string } | undefined>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    setData: (state, action: PayloadAction<NotificationsPage>) => ({
      ...state,
      items: action.payload.items,
      nextCursor: action.payload.nextCursor,
      loading: false,
    }),
    appendData: (state, action: PayloadAction<NotificationsPage>) => ({
      ...state,
      items: [...state.items, ...action.payload.items],
      nextCursor: action.payload.nextCursor,
      loading: false,
    }),

    fetchUnreadCount: (state) => state,
    setUnreadCount: (state, action: PayloadAction<number>) => ({
      ...state,
      unreadCount: action.payload,
    }),

    markRead: (
      state,
      action: PayloadAction<{ kind: NotificationKind; id: number }>,
    ) => state,
    markReadSuccess: (
      state,
      action: PayloadAction<{ kind: NotificationKind; id: number }>,
    ) => {
      const target = state.items.find((i) => sameItem(i, action.payload));
      if (!target || target.isRead) return state;

      return {
        ...state,
        items: state.items.map((i) =>
          sameItem(i, action.payload) ? { ...i, isRead: true } : i,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    },

    markAllRead: (state) => state,
    markAllReadSuccess: (state) => ({
      ...state,
      items: state.items.map((i) => ({ ...i, isRead: true })),
      unreadCount: 0,
    }),

    notificationReceived: (state, action: PayloadAction<NotificationItem>) => {
      if (state.items.some((i) => sameItem(i, action.payload))) return state;

      return {
        ...state,
        items: [action.payload, ...state.items],
        unreadCount: state.unreadCount + 1,
      };
    },
  },
});

export { notificationsSlice };
export const notificationsActions = notificationsSlice.actions;
export default notificationsSlice.reducer;
